import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { it as itLocale } from "date-fns/locale";
import {
  ArrowLeft, Clock, Mountain, Loader2, Globe, Shield, Minus, Plus,
  AlertCircle, CalendarDays, Users, User, Sparkles,
} from "lucide-react";
import { useLang } from "@/i18n";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Safe helpers ── */
function safeNumber(val, fallback) {
  const n = Number(val);
  return Number.isFinite(n) ? n : fallback;
}

function safeDateLabel(str, fmt, opts) {
  try {
    const d = parseISO(String(str));
    if (d instanceof Date && !isNaN(d.getTime())) return format(d, fmt, opts || {});
  } catch { /* ignore */ }
  return String(str || "");
}

export default function MottaronePage() {
  const navigate = useNavigate();
  const { t, toggleLang, lang } = useLang();
  const isIt = lang === "it";
  const calendarLocale = isIt ? itLocale : undefined;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [dates, setDates] = useState([]);
  const [settings, setSettings] = useState(null);

  const [selectedDate, setSelectedDate] = useState(null);
  const [variant, setVariant] = useState("");
  const [numQuads, setNumQuads] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [licenseBConfirmed, setLicenseBConfirmed] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });

  useEffect(() => {
    let cancelled = false;
    const fetchInfo = async () => {
      try {
        const res = await axios.get(`${API}/mottarone/info`);
        if (cancelled) return;
        const raw = Array.isArray(res?.data?.dates) ? res.data.dates : [];
        const validated = [];
        for (const d of raw) {
          if (d && typeof d === "object" && d.tour_date) {
            validated.push({
              tour_date: String(d.tour_date),
              time_slot: String(d.time_slot || "09:00"),
              price_single: safeNumber(d.price_single, 200),
              price_double: safeNumber(d.price_double, 280),
              duration: String(d.duration || ""),
              available_quads: safeNumber(d.available_quads, 0),
              max_quads: safeNumber(d.max_quads, 7),
            });
          }
        }
        setDates(validated);
        if (res?.data?.settings && typeof res.data.settings === "object") {
          setSettings(res.data.settings);
        }
      } catch {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchInfo();
    return () => { cancelled = true; };
  }, []);

  const description = isIt
    ? (settings?.description_it || t("tourMottaroneDesc"))
    : (settings?.description_en || t("tourMottaroneDesc"));

  const unitPrice = selectedDate
    ? (variant === "single" ? selectedDate.price_single : selectedDate.price_double)
    : 0;
  const totalPrice = variant ? unitPrice * numQuads : 0;
  const maxAvail = safeNumber(selectedDate?.available_quads, 1);

  const isFormComplete = Boolean(
    selectedDate && variant && form.name && form.email && form.phone &&
    licenseBConfirmed && termsAccepted && privacyAccepted
  );

  const handleSelectDate = (d) => {
    setSelectedDate(d);
    setVariant("");
    setNumQuads(1);
  };

  const handleSubmit = async () => {
    try {
      if (!selectedDate || !variant) { toast.error(t("selectDateTime")); return; }
      if (!form.name || !form.email || !form.phone) { toast.error(t("fillAllFields")); return; }
      if (!licenseBConfirmed) { toast.error(t("licenseBError")); return; }
      if (!termsAccepted) { toast.error(t("termsError")); return; }
      if (!privacyAccepted) { toast.error(t("privacyError")); return; }

      setSubmitting(true);
      const res = await axios.post(`${API}/checkout`, {
        tour_type: variant === "single" ? "mot_single" : "mot_double",
        booking_date: selectedDate.tour_date,
        time_slot: selectedDate.time_slot,
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
        num_quads: numQuads,
        include_video: false,
        license_b_confirmed: licenseBConfirmed,
        language: lang,
        origin_url: window.location.origin,
      });

      const url = res?.data?.url;
      if (typeof url === "string" && url.length > 0) {
        window.location.href = url;
      } else {
        toast.error(t("mottaroneErrorBooking"));
        setSubmitting(false);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("mottaroneErrorBooking"));
      setSubmitting(false);
    }
  };

  const CheckRow = ({ checked, onToggle, label, sub, testId }) => (
    <button
      data-testid={testId}
      onClick={onToggle}
      className={`w-full flex items-center gap-3 p-4 border rounded-lg transition-all ${
        checked ? "bg-[#d4a056]/10 border-[#d4a056]" : "glass-card hover:border-[#d4a056]/40"
      }`}
    >
      <div className={`w-5 h-5 border-2 flex items-center justify-center rounded flex-shrink-0 ${
        checked ? "bg-[#d4a056] border-[#d4a056]" : "border-[#5a7a5a]"
      }`}>
        {checked && <span className="text-[#0a1a0f] text-xs font-bold">✓</span>}
      </div>
      <div className="text-left">
        <span className="text-sm text-[#e8f0e8] font-semibold block">{label}</span>
        <span className="text-xs text-[#8faa8f] flex items-center gap-1 mt-0.5">
          <Shield className="w-3 h-3 text-[#d4a056]" /> {sub}
        </span>
      </div>
    </button>
  );

  return (
    <div className="min-h-screen bg-[#0a1a0f] text-[#e8f0e8]">
      {/* Header */}
      <header className="border-b border-[#d4a056]/30 px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <button
            data-testid="back-btn"
            onClick={() => navigate("/")}
            className="text-[#8faa8f] hover:text-[#d4a056] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#d4a056]" />
            <span className="font-bold uppercase tracking-wider text-sm text-[#d4a056]">
              {t("tourMottaroneName")}
            </span>
            <span className="hidden sm:inline-block text-[10px] px-2 py-0.5 bg-[#d4a056]/10 border border-[#d4a056]/40 text-[#d4a056] uppercase font-bold tracking-widest rounded-full">
              {t("mottaroneBadge")}
            </span>
          </div>
          <div className="ml-auto">
            <button
              data-testid="lang-toggle-mottarone"
              onClick={toggleLang}
              className="flex items-center gap-1 px-2 py-1 rounded-full glass-card text-xs text-[#d4a056]"
            >
              <Globe className="w-3 h-3" /> {t("langToggle")}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <h1
          className="text-3xl md:text-5xl font-bold uppercase tracking-tight mb-2 animate-slide-up text-[#d4a056] text-glow-gold"
          data-testid="mottarone-title"
        >
          {t("tourMottaroneName")}
        </h1>
        <p className="text-[#c0d4c0] max-w-2xl mb-3 animate-slide-up-delay-1" data-testid="mottarone-description">
          {description}
        </p>
        <p className="flex items-center gap-2 text-sm text-[#d4a056] mb-10 animate-slide-up-delay-1">
          <AlertCircle className="w-4 h-4" /> {t("mottaroneAdvance")}
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-[#8faa8f] py-12 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> {t("loading")}
          </div>
        ) : loadError ? (
          <div className="glass-card rounded-xl p-8 text-center max-w-lg mx-auto" data-testid="mottarone-load-error">
            <AlertCircle className="w-8 h-8 text-red-400/80 mx-auto mb-3" />
            <p className="text-[#8faa8f]">
              {isIt ? "Impossibile caricare le date. Riprova più tardi." : "Unable to load dates. Please try again later."}
            </p>
          </div>
        ) : dates.length === 0 ? (
          <div className="glass-card rounded-xl p-10 text-center max-w-lg mx-auto border border-[#d4a056]/30" data-testid="mottarone-no-dates">
            <Mountain className="w-10 h-10 text-[#d4a056] mx-auto mb-4" />
            <p className="text-[#c0d4c0] mb-6">{t("mottaroneNoDates")}</p>
            <button
              data-testid="mottarone-back-home"
              onClick={() => navigate("/")}
              className="border-2 border-[#d4a056] text-[#d4a056] font-bold uppercase tracking-wider px-8 py-3 hover:bg-[#d4a056] hover:text-[#0a1a0f] transition-all text-sm rounded-lg"
            >
              {t("backHome")}
            </button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left: available dates */}
            <div className="animate-slide-up-delay-2">
              <label className="text-sm font-bold uppercase tracking-widest text-[#d4a056] mb-4 block">
                {t("mottaroneStepDate")}
              </label>
              <div className="space-y-3" data-testid="mottarone-dates-list">
                {dates.map((d) => {
                  const isSelected = selectedDate?.tour_date === d.tour_date;
                  const soldOut = d.available_quads <= 0;
                  return (
                    <button
                      key={d.tour_date}
                      data-testid={`mottarone-date-${d.tour_date}`}
                      disabled={soldOut}
                      onClick={() => handleSelectDate(d)}
                      className={`w-full text-left p-4 border rounded-xl transition-all ${
                        isSelected
                          ? "bg-[#d4a056]/10 border-[#d4a056]"
                          : soldOut
                            ? "bg-[#0a1a0f]/50 border-[#1a2e20] opacity-50 cursor-not-allowed"
                            : "glass-card hover:border-[#d4a056]/60"
                      }`}
                    >
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <span className="flex items-center gap-2 font-bold text-lg text-[#e8f0e8] capitalize">
                            <CalendarDays className="w-4 h-4 text-[#d4a056]" />
                            {safeDateLabel(d.tour_date, "EEEE d MMMM yyyy", { locale: calendarLocale })}
                          </span>
                          <span className="flex items-center gap-3 text-xs text-[#8faa8f] mt-1.5">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3 text-[#d4a056]" /> {d.time_slot}
                            </span>
                            {d.duration && <span>{d.duration}</span>}
                            <span>
                              {soldOut
                                ? (isIt ? "Esaurito" : "Sold out")
                                : `${d.available_quads} ${t("quadAvailable")}`}
                            </span>
                          </span>
                        </div>
                        <div className="text-right">
                          <span className="block text-[#d4a056] font-bold">€{d.price_single} <span className="text-[10px] text-[#8faa8f] uppercase font-normal">{isIt ? "singolo" : "single"}</span></span>
                          <span className="block text-[#d4a056] font-bold">€{d.price_double} <span className="text-[10px] text-[#8faa8f] uppercase font-normal">{isIt ? "doppio" : "double"}</span></span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right: variant + quads + form */}
            <div className="space-y-8">
              {selectedDate && (
                <div data-testid="mottarone-variant-section" className="animate-fade-in">
                  <label className="text-sm font-bold uppercase tracking-widest text-[#d4a056] mb-4 block">
                    {t("mottaroneStepType")}
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      data-testid="mottarone-variant-single"
                      onClick={() => { setVariant("single"); setNumQuads(1); }}
                      className={`p-4 border rounded-xl text-center transition-all ${
                        variant === "single" ? "bg-[#d4a056] border-[#d4a056] text-[#0a1a0f]" : "glass-card text-[#e8f0e8] hover:border-[#d4a056]/60"
                      }`}
                    >
                      <User className="w-5 h-5 mx-auto mb-1" />
                      <span className="font-bold block text-sm uppercase">{t("mottaroneSingleLabel")}</span>
                      <span className="block text-xs mt-0.5 opacity-80">{t("mottaroneSingleSub")}</span>
                      <span className="block text-xl font-bold mt-1.5">€{selectedDate.price_single}</span>
                    </button>
                    <button
                      data-testid="mottarone-variant-double"
                      onClick={() => { setVariant("double"); setNumQuads(1); }}
                      className={`p-4 border rounded-xl text-center transition-all ${
                        variant === "double" ? "bg-[#d4a056] border-[#d4a056] text-[#0a1a0f]" : "glass-card text-[#e8f0e8] hover:border-[#d4a056]/60"
                      }`}
                    >
                      <Users className="w-5 h-5 mx-auto mb-1" />
                      <span className="font-bold block text-sm uppercase">{t("mottaroneDoubleLabel")}</span>
                      <span className="block text-xs mt-0.5 opacity-80">{t("mottaroneDoubleSub")}</span>
                      <span className="block text-xl font-bold mt-1.5">€{selectedDate.price_double}</span>
                    </button>
                  </div>
                </div>
              )}

              {variant && (
                <div data-testid="mottarone-quads-section" className="animate-fade-in">
                  <label className="text-sm font-bold uppercase tracking-widest text-[#d4a056] mb-3 block">
                    {t("mottaroneStepQuads")}
                  </label>
                  <div className="flex items-center gap-4">
                    <button
                      data-testid="mottarone-quads-minus"
                      onClick={() => setNumQuads((n) => Math.max(1, n - 1))}
                      disabled={numQuads <= 1}
                      className="w-10 h-10 rounded-lg glass-card flex items-center justify-center text-[#8faa8f] hover:text-[#d4a056] disabled:opacity-30 transition-colors"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="text-3xl font-bold text-[#d4a056] min-w-[3rem] text-center" data-testid="mottarone-quads-count">
                      {numQuads}
                    </span>
                    <button
                      data-testid="mottarone-quads-plus"
                      onClick={() => setNumQuads((n) => Math.min(maxAvail, n + 1))}
                      disabled={numQuads >= maxAvail}
                      className="w-10 h-10 rounded-lg glass-card flex items-center justify-center text-[#8faa8f] hover:text-[#d4a056] disabled:opacity-30 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                    <span className="text-sm text-[#5a7a5a]">/ {maxAvail} {t("quadAvailable")}</span>
                  </div>
                </div>
              )}

              {variant && (
                <div data-testid="mottarone-customer-form" className="animate-fade-in">
                  <label className="text-sm font-bold uppercase tracking-widest text-[#d4a056] mb-4 block">
                    {t("mottaroneStepData")}
                  </label>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-[#8faa8f] text-xs uppercase tracking-wider">{t("fullName")}</Label>
                      <Input
                        data-testid="mottarone-input-name"
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                        placeholder="Mario Rossi"
                        className="bg-[#0f2518] border-[#1a4a2e] text-[#e8f0e8] focus:border-[#d4a056] focus:ring-[#d4a056] rounded-lg h-12 mt-1 placeholder:text-[#3a5a3a]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#8faa8f] text-xs uppercase tracking-wider">{t("email")}</Label>
                      <Input
                        data-testid="mottarone-input-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                        placeholder="mario@email.com"
                        className="bg-[#0f2518] border-[#1a4a2e] text-[#e8f0e8] focus:border-[#d4a056] focus:ring-[#d4a056] rounded-lg h-12 mt-1 placeholder:text-[#3a5a3a]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#8faa8f] text-xs uppercase tracking-wider">{t("phone")}</Label>
                      <Input
                        data-testid="mottarone-input-phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                        placeholder="+39 333 1234567"
                        className="bg-[#0f2518] border-[#1a4a2e] text-[#e8f0e8] focus:border-[#d4a056] focus:ring-[#d4a056] rounded-lg h-12 mt-1 placeholder:text-[#3a5a3a]"
                      />
                    </div>

                    <div className="pt-2 space-y-4">
                      <CheckRow
                        checked={licenseBConfirmed}
                        onToggle={() => setLicenseBConfirmed(!licenseBConfirmed)}
                        label={t("licenseBLabel")}
                        sub={t("licenseBRequired")}
                        testId="mottarone-license-b-checkbox"
                      />
                      <CheckRow
                        checked={termsAccepted}
                        onToggle={() => setTermsAccepted(!termsAccepted)}
                        label={t("termsLabel")}
                        sub={t("termsRequired")}
                        testId="mottarone-terms-checkbox"
                      />
                      <CheckRow
                        checked={privacyAccepted}
                        onToggle={() => setPrivacyAccepted(!privacyAccepted)}
                        label={t("privacyLabel")}
                        sub={t("privacyRequired")}
                        testId="mottarone-privacy-checkbox"
                      />
                    </div>
                  </div>
                </div>
              )}

              {isFormComplete && (
                <div data-testid="mottarone-booking-summary" className="glass-card rounded-xl p-6 animate-fade-in border border-[#d4a056]/40">
                  <h3 className="text-lg font-bold uppercase text-[#d4a056] mb-4">{t("summary")}</h3>
                  <div className="space-y-2 text-sm text-[#8faa8f]">
                    <div className="flex justify-between">
                      <span>{t("tour")}</span>
                      <span className="text-[#e8f0e8]">
                        {t("tourMottaroneName")} — {variant === "single" ? t("mottaroneSingleLabel") : t("mottaroneDoubleLabel")}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t("date")}</span>
                      <span className="text-[#e8f0e8] capitalize">
                        {safeDateLabel(selectedDate.tour_date, "d MMMM yyyy", { locale: calendarLocale })}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t("time")}</span>
                      <span className="text-[#e8f0e8]">{selectedDate.time_slot}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t("quads")}</span>
                      <span className="text-[#e8f0e8]">{numQuads} x €{unitPrice}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t("name")}</span>
                      <span className="text-[#e8f0e8]">{form.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t("email")}</span>
                      <span className="text-[#e8f0e8] text-xs break-all">{form.email}</span>
                    </div>
                    <div className="border-t border-[#1a4a2e] pt-2 mt-2 flex justify-between text-lg font-bold">
                      <span className="text-[#e8f0e8]">{t("total")}</span>
                      <span className="text-[#d4a056] text-glow-gold">€{totalPrice}</span>
                    </div>
                  </div>
                  <button
                    data-testid="mottarone-confirm-booking-btn"
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="w-full mt-6 bg-[#d4a056] text-[#0a1a0f] font-bold uppercase tracking-wider py-4 hover:bg-[#e0b86e] transition-all active:scale-[0.98] text-sm disabled:opacity-50 rounded-lg"
                  >
                    {submitting ? (
                      <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                    ) : (
                      t("payConfirm")
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
